import { moveSlidingToggle } from './sliding-toggle.js';
import { initializeQuickBuyCard } from './quick-buy-card.js';
const SELECTORS = {
  root: '[data-gift-guide]',
  filters: '[data-gift-guide-filters]',
  card: '[data-gift-guide-card]',
  row: '[data-gift-guide-row]',
  count: '[data-gift-guide-count]',
  empty: '[data-gift-guide-empty]',
  emptyCopy: '[data-gift-guide-empty-copy]',
  reset: '[data-gift-guide-reset]',
};

const FILTER_PARAMS = Object.freeze({
  budget: 'gift_budget',
  category: 'gift_category',
});
const FILTER_VALUES = Object.freeze({
  budget: new Set(['all', 'up-to-25', 'under-50', 'under-100', 'under-250', '250-plus']),
  category: new Set(['all', 'coffee', 'equipment', 'accessories']),
});
const RETURN_STATE_KEY = 'sigma-gift-guide-return-v1';
const RETURN_STATE_MAX_AGE = 2 * 60 * 60 * 1000;
const instances = new Map();
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const readReturnState = () => {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(RETURN_STATE_KEY) || 'null');
    if (!value || typeof value !== 'object' || typeof value.guideUrl !== 'string'
      || !Number.isFinite(value.createdAt) || Date.now() - value.createdAt > RETURN_STATE_MAX_AGE
      || !Number.isFinite(value.scrollY) || !value.rows || typeof value.rows !== 'object') {
      return null;
    }
    return value;
  } catch {
    return null;
  }
};

const writeReturnState = (value) => {
  try {
    window.sessionStorage.setItem(RETURN_STATE_KEY, JSON.stringify(value));
  } catch {}
};

const clearReturnState = () => {
  try {
    window.sessionStorage.removeItem(RETURN_STATE_KEY);
  } catch {}
};

const currentGuideUrl = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;

const isProductUrl = (href) => {
  try {
    const url = new URL(href, window.location.href);
    return url.origin === window.location.origin && /(?:^|\/)products\/[^/]+\/?$/.test(url.pathname);
  } catch {
    return false;
  }
};

const matchesBudget = (minimumPrice, maximumPrice, budget) => {
  switch (budget) {
    case 'up-to-25': return minimumPrice <= 2500;
    case 'under-50': return minimumPrice < 5000;
    case 'under-100': return minimumPrice < 10000;
    case 'under-250': return minimumPrice < 25000;
    case '250-plus': return maximumPrice >= 25000;
    default: return true;
  }
};

const variantMatchesBudget = (price, budget) => {
  if (!Number.isFinite(price)) return false;
  switch (budget) {
    case 'up-to-25': return price <= 2500;
    case 'under-50': return price < 5000;
    case 'under-100': return price < 10000;
    case 'under-250': return price < 25000;
    case '250-plus': return price >= 25000;
    default: return true;
  }
};

const syncCardVariantToBudget = (card, budget) => {
  const eligibilityData = card.querySelector('[data-gift-guide-variants]');
  if (!(eligibilityData instanceof HTMLScriptElement)) return;

  let eligibleVariants;
  try {
    eligibleVariants = JSON.parse(eligibilityData.textContent || '[]');
  } catch {
    return;
  }

  const eligible = Array.isArray(eligibleVariants) ? eligibleVariants
    .filter((variant) => variant.inStock && variantMatchesBudget(Number(variant.priceMinorUnits), budget))
    .sort((first, second) => Number(first.priceMinorUnits) - Number(second.priceMinorUnits)) : [];
  const quickBuyCard = card.querySelector('[data-quick-buy-card]');
  const quickBuyControl = quickBuyCard?.querySelector('[data-quick-buy-variant]');
  const productCard = card.querySelector('[data-product-card-showcase]');
  const currentId = quickBuyControl instanceof HTMLInputElement || quickBuyControl instanceof HTMLSelectElement
    ? quickBuyControl.value
    : productCard?.dataset.selectedVariantId;
  const preferredId = card.dataset.preferredVariantId;
  const selected = eligible.find((variant) => String(variant.id) === currentId)
    || eligible.find((variant) => String(variant.id) === preferredId)
    || eligible[0];
  if (!selected) return;

  if (quickBuyCard instanceof HTMLElement
    && (quickBuyControl instanceof HTMLInputElement || quickBuyControl instanceof HTMLSelectElement)) {
    initializeQuickBuyCard(quickBuyCard);
    if (quickBuyControl.value !== String(selected.id)) {
      quickBuyControl.value = String(selected.id);
      quickBuyControl.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return;
  }

  const variantData = productCard?.querySelector('[data-product-card-variants]');
  if (!(productCard instanceof HTMLElement) || !(variantData instanceof HTMLScriptElement)) return;

  let presentationVariants;
  try {
    presentationVariants = JSON.parse(variantData.textContent || '[]');
  } catch {
    return;
  }

  const presentation = Array.isArray(presentationVariants)
    ? presentationVariants.find((variant) => String(variant.id) === String(selected.id))
    : null;
  if (!presentation || String(selected.id) === productCard.dataset.selectedVariantId) return;

  productCard.dataset.selectedVariantId = String(selected.id);
  productCard.querySelectorAll('[data-product-card-link]').forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;
    const url = new URL(link.href, window.location.href);
    url.searchParams.set('variant', String(selected.id));
    link.href = url;
  });
  productCard.querySelectorAll('[data-product-card-add-id]').forEach((control) => {
    if (control instanceof HTMLInputElement || control instanceof HTMLSelectElement) {
      control.value = String(selected.id);
    }
  });

  const price = productCard.querySelector('[data-product-card-price]');
  const priceText = productCard.querySelector('[data-product-card-price-text]');
  if (price instanceof HTMLElement) price.textContent = presentation.price || '';
  if (priceText instanceof HTMLElement) priceText.textContent = presentation.price || '';

  const stock = productCard.querySelector('[data-product-card-stock]');
  if (stock instanceof HTMLElement) {
    const quantity = Number(presentation.inventoryQuantity);
    const lowStock = Boolean(presentation.inventoryManagement && quantity > 0 && quantity <= 3);
    stock.hidden = !lowStock;
    stock.classList.toggle('is-low-stock', lowStock);
    stock.textContent = lowStock ? `Only ${quantity} left!` : '';
  }
};

const initializeCarousel = (row, signal) => {
  const track = row.querySelector('[data-product-track]');
  const slides = [...row.querySelectorAll('[data-gift-guide-slide]')];
  const previousButton = row.querySelector('[data-product-previous]');
  const nextButton = row.querySelector('[data-product-next]');
  const controls = row.querySelector('[data-product-controls]');
  const status = row.querySelector('[data-product-carousel-status]');
  const label = row.dataset.productCarouselLabel || 'Gifts';
  let activeSlideIndex = 0;
  let updateFrame = 0;
  let statusTimer = 0;

  if (!(track instanceof HTMLElement) || slides.length === 0) return null;

  const visibleSlides = () => slides.filter((slide) => !slide.hidden);
  const slideOffset = (slide) => slide.offsetLeft - track.offsetLeft;
  const pageSize = (visible) => {
    if (!visible.length) return 1;
    const styles = window.getComputedStyle(track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    return Math.max(1, Math.round((track.clientWidth + gap) / (visible[0].getBoundingClientRect().width + gap)));
  };
  const closestIndex = (visible) => visible.reduce((closest, slide, index) => {
    const closestDistance = Math.abs(slideOffset(visible[closest]) - track.scrollLeft);
    const distance = Math.abs(slideOffset(slide) - track.scrollLeft);
    return distance < closestDistance ? index : closest;
  }, 0);

  const update = ({ announce = false } = {}) => {
    const visible = visibleSlides();
    const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const hasOverflow = !row.hidden && visible.length > 1 && maximumScroll > 1;
    activeSlideIndex = visible.length ? closestIndex(visible) : 0;
    const visiblePageSize = pageSize(visible);
    const pageEnd = Math.min(activeSlideIndex + visiblePageSize, visible.length);

    if (controls) controls.hidden = !hasOverflow;
    if (previousButton) previousButton.disabled = !hasOverflow || track.scrollLeft <= 1;
    if (nextButton) nextButton.disabled = !hasOverflow || track.scrollLeft >= maximumScroll - 1;
    slides.forEach((slide) => slide.classList.remove('is-active'));
    visible[activeSlideIndex]?.classList.add('is-active');

    if (status) {
      status.hidden = !hasOverflow;
      const nextStatus = visiblePageSize > 1
        ? `${label} ${activeSlideIndex + 1}–${pageEnd} of ${visible.length}`
        : `${label} ${activeSlideIndex + 1} of ${visible.length}`;
      window.clearTimeout(statusTimer);
      if (announce) statusTimer = window.setTimeout(() => { status.textContent = nextStatus; }, 140);
      else status.textContent = nextStatus;
    }
  };

  const requestUpdate = (options) => {
    if (updateFrame) return;
    updateFrame = window.requestAnimationFrame(() => {
      updateFrame = 0;
      update(options);
    });
  };
  const showSlide = (index) => {
    const visible = visibleSlides();
    const slide = visible[Math.max(0, Math.min(index, visible.length - 1))];
    if (!slide) return;
    track.scrollTo({ left: slideOffset(slide), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  };

  previousButton?.addEventListener('click', () => showSlide(activeSlideIndex - pageSize(visibleSlides())), { signal });
  nextButton?.addEventListener('click', () => showSlide(activeSlideIndex + pageSize(visibleSlides())), { signal });
  track.addEventListener('scroll', () => requestUpdate({ announce: true }), { passive: true, signal });
  track.addEventListener('keydown', (event) => {
    if (event.target !== track) return;
    const visible = visibleSlides();
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      const direction = event.key === 'ArrowLeft' ? -1 : 1;
      showSlide(activeSlideIndex + (direction * pageSize(visible)));
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      showSlide(event.key === 'Home' ? 0 : visible.length - 1);
    }
  }, { signal });

  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => requestUpdate()) : null;
  resizeObserver?.observe(track);
  window.addEventListener('resize', requestUpdate, { passive: true, signal });

  return {
    cleanup: () => {
      window.cancelAnimationFrame(updateFrame);
      window.clearTimeout(statusTimer);
      resizeObserver?.disconnect();
    },
    read: () => track.scrollLeft,
    restore: (left) => {
      if (Number.isFinite(left)) track.scrollTo({ left, behavior: 'auto' });
    },
    reset: () => track.scrollTo({ left: 0, behavior: 'auto' }),
    update: requestUpdate,
  };
};

const initGiftGuide = (root) => {
  if (!(root instanceof HTMLElement) || instances.has(root)) return;
  const filters = root.querySelector(SELECTORS.filters);
  // Shuffle once before carousel setup; filters keep this order stable.
  for (const track of root.querySelectorAll('.gift-guide__track')) {
    const coffee = [...track.querySelectorAll('[data-gift-guide-card][data-category="coffee"]')];
    for (let i = coffee.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [coffee[i], coffee[j]] = [coffee[j], coffee[i]];
    }
    for (const card of coffee.slice(0, 12)) track.append(card);
    for (const card of coffee.slice(12)) card.remove();
  }
  const cards = [...root.querySelectorAll(SELECTORS.card)];
  const rows = [...root.querySelectorAll(SELECTORS.row)];
  const count = root.querySelector(SELECTORS.count);
  const empty = root.querySelector(SELECTORS.empty);
  const emptyCopy = root.querySelector(SELECTORS.emptyCopy);
  const resetButtons = [...root.querySelectorAll(SELECTORS.reset)];
  if (!(filters instanceof HTMLElement) || cards.length === 0) return;

  const abort = new AbortController();
  const { signal } = abort;
  const carousels = rows.map((row) => initializeCarousel(row, signal));
  let budget = 'all';
  let category = 'all';

  const availableValues = (selector, dataKey) => new Set(
    [...filters.querySelectorAll(selector)].map((button) => button.dataset[dataKey]).filter(Boolean),
  );
  const availableBudgets = availableValues('[data-gift-guide-budget]', 'giftGuideBudget');
  const availableCategories = availableValues('[data-gift-guide-category]', 'giftGuideCategory');

  const readUrlFilters = () => {
    const params = new URL(window.location.href).searchParams;
    const requestedBudget = params.get(FILTER_PARAMS.budget);
    const requestedCategory = params.get(FILTER_PARAMS.category);
    return {
      budget: requestedBudget && FILTER_VALUES.budget.has(requestedBudget) && availableBudgets.has(requestedBudget)
        ? requestedBudget
        : 'all',
      category: requestedCategory && FILTER_VALUES.category.has(requestedCategory) && availableCategories.has(requestedCategory)
        ? requestedCategory
        : 'all',
      needsSanitizing: Boolean(
        (requestedBudget && (!FILTER_VALUES.budget.has(requestedBudget) || !availableBudgets.has(requestedBudget)))
        || (requestedCategory && (!FILTER_VALUES.category.has(requestedCategory) || !availableCategories.has(requestedCategory))),
      ),
    };
  };

  const writeUrlFilters = ({ replace = false } = {}) => {
    const url = new URL(window.location.href);
    if (budget === 'all') url.searchParams.delete(FILTER_PARAMS.budget);
    else url.searchParams.set(FILTER_PARAMS.budget, budget);
    if (category === 'all') url.searchParams.delete(FILTER_PARAMS.category);
    else url.searchParams.set(FILTER_PARAMS.category, category);
    const method = replace ? 'replaceState' : 'pushState';
    window.history[method](window.history.state, '', url);
  };

  const labelFor = (selector, dataKey, value) => {
    const button = [...filters.querySelectorAll(selector)].find((candidate) => candidate.dataset[dataKey] === value);
    return button?.getAttribute('aria-label') || button?.textContent?.trim() || value;
  };

  const setPressed = (selector, value, dataKey, animate = false) => {
    const buttons = [...filters.querySelectorAll(selector)];
    buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset[dataKey] === value)));
    const activeIndex = buttons.findIndex((button) => button.getAttribute('aria-pressed') === 'true');
    const indicator = buttons[0]?.closest('.gift-guide__filter-options')?.querySelector('.gift-guide__filter-indicator');
    moveSlidingToggle(indicator, Math.max(0, activeIndex), animate);
    const activeButton = buttons[activeIndex];
    const scroll = activeButton?.closest('.gift-guide__filter-scroll');
    if (scroll) {
      const start = activeButton.offsetLeft;
      const end = start + activeButton.offsetWidth;
      const left = start < scroll.scrollLeft ? start
        : end > scroll.scrollLeft + scroll.clientWidth ? end - scroll.clientWidth : scroll.scrollLeft;
      scroll.scrollTo({ left, behavior: animate && !reducedMotion.matches ? 'smooth' : 'instant' });
    }
  };

  const matchesFilters = (card) => {
    const minimumPrice = Number.parseInt(card.dataset.price || '', 10);
    const maximumPrice = Number.parseInt(card.dataset.priceMax || '', 10);
    return (category === 'all' || card.dataset.category === category)
      && Number.isFinite(minimumPrice)
      && Number.isFinite(maximumPrice)
      && matchesBudget(minimumPrice, maximumPrice, budget);
  };

  const collectionLinks = [...root.querySelectorAll('[data-gift-guide-collection-url]')];
  const syncCollectionLinks = () => {
    const ceiling = { 'up-to-25': 25, 'under-50': 49.99, 'under-100': 99.99, 'under-250': 249.99 }[budget];
    collectionLinks.forEach((link) => {
      const url = new URL(link.dataset.giftGuideCollectionUrl, window.location.href);
      url.searchParams.set('filter.v.availability', '1');
      if (ceiling !== undefined) {
        const original = url.searchParams.get('filter.v.price.lte');
        url.searchParams.set('filter.v.price.lte', String(original === null ? ceiling : Math.min(Number(original), ceiling)));
      }
      if (budget === '250-plus') {
        const original = url.searchParams.get('filter.v.price.gte');
        url.searchParams.set('filter.v.price.gte', String(Math.max(Number(original || 0), 250)));
      }
      link.href = url.href;
    });
  };

  const applyFilters = ({ resetTracks = false, changedRows = new Set() } = {}) => {
    syncCollectionLinks();
    const visibleProducts = new Set();
    cards.forEach((card) => {
      const visible = matchesFilters(card);
      card.hidden = !visible;
      if (visible) {
        visibleProducts.add(card.dataset.productId || card);
        syncCardVariantToBudget(card, budget);
      }
    });
    const visibleCount = visibleProducts.size;
    rows.forEach((row) => { row.hidden = !row.querySelector(`${SELECTORS.card}:not([hidden])`); });
    if (count) count.textContent = `${visibleCount} ${visibleCount === 1 ? 'gift' : 'gifts'}`;
    if (empty) empty.hidden = visibleCount !== 0;
    if (visibleCount === 0 && emptyCopy) {
      const budgetLabel = labelFor('[data-gift-guide-budget]', 'giftGuideBudget', budget);
      const categoryLabel = labelFor('[data-gift-guide-category]', 'giftGuideCategory', category);
      if (budget !== 'all' && category !== 'all') {
        emptyCopy.textContent = `No ${categoryLabel.toLowerCase()} gifts are currently available ${budget === '250-plus' ? 'at £250 or more' : budgetLabel.toLowerCase()}. Clear either filter or show all gifts.`;
      } else if (budget !== 'all') {
        emptyCopy.textContent = `No gifts are currently available ${budget === '250-plus' ? 'at £250 or more' : budgetLabel.toLowerCase()}. Clear the budget or show all gifts.`;
      } else if (category !== 'all') {
        emptyCopy.textContent = `No ${categoryLabel.toLowerCase()} gifts are currently available. Clear the category or show all gifts.`;
      } else {
        emptyCopy.textContent = 'No gifts are currently available. Please try again later.';
      }
    }
    resetButtons.forEach((button) => {
      const target = button.dataset.giftGuideReset;
      button.hidden = (target === 'budget' && budget === 'all')
        || (target === 'category' && category === 'all')
        || (target === 'all' && budget === 'all' && category === 'all');
    });
    carousels.forEach((carousel, index) => {
      if (resetTracks && changedRows.has(rows[index])) carousel?.reset();
      carousel?.update();
    });
  };

  let filterFrame = 0;
  let filterTimer = 0;
  let transitionId = 0;
  const rowRegion = root.querySelector('[data-gift-guide-rows]');
  const rowAnimations = new Set();
  const stopRowMotion = () => {
    rowAnimations.forEach((animation) => animation.cancel());
    rowAnimations.clear();
    rows.forEach((row) => { row.inert = false; });
    rowRegion?.removeAttribute('aria-busy');
  };
  const motionTargets = (changedRows) => rows.flatMap((row) => {
    if (row.hidden || !changedRows.has(row)) return [];
    return [row.querySelector('.gift-guide__row-header'), ...row.querySelectorAll(`${SELECTORS.card}:not([hidden])`)];
  }).filter((element) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight
      && rect.right > 0 && rect.left < window.innerWidth;
  });
  const animateRows = (targets, entering) => Promise.allSettled(targets.map((element, index) => {
    const animation = element.animate(
      entering
        ? [{ opacity: 0, transform: 'translateY(4px)' }, { opacity: 1, transform: 'translateY(0)' }]
        : [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-2px)' }],
      {
        duration: entering ? 180 : 100,
        delay: entering ? Math.min(index * 25, 75) : 0,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'both',
      },
    );
    rowAnimations.add(animation);
    return animation.finished;
  }));
  const update = async ({ resetTracks = false, animate = false } = {}) => {
    const id = ++transitionId;
    cancelAnimationFrame(filterFrame);
    clearTimeout(filterTimer);
    stopRowMotion();
    // Compare product membership per row, not the selected filter labels.
    const changedRows = new Set(rows.filter((row) =>
      [...row.querySelectorAll(SELECTORS.card)].some((card) => card.hidden === matchesFilters(card)),
    ));
    if (!animate || changedRows.size === 0 || reducedMotion.matches || typeof Element.prototype.animate !== 'function') {
      applyFilters({ resetTracks, changedRows });
      return;
    }
    rowRegion?.setAttribute('aria-busy', 'true');
    changedRows.forEach((row) => { row.inert = true; });
    await animateRows(motionTargets(changedRows), false);
    if (id !== transitionId || signal.aborted) return;
    stopRowMotion();
    applyFilters({ resetTracks, changedRows });
    rowRegion?.setAttribute('aria-busy', 'true');
    await animateRows(motionTargets(changedRows), true);
    if (id === transitionId) stopRowMotion();
  };
  reducedMotion.addEventListener('change', () => {
    if (reducedMotion.matches) update({ resetTracks: true });
  }, { signal });
  filters.addEventListener('click', (event) => {
    const button = event.target.closest('button');
    if (!(button instanceof HTMLButtonElement)) return;
    const previousBudget = budget;
    const previousCategory = category;
    if (button.dataset.giftGuideBudget) {
      budget = button.dataset.giftGuideBudget;
      setPressed('[data-gift-guide-budget]', budget, 'giftGuideBudget', true);
    }
    if (button.dataset.giftGuideCategory) {
      category = button.dataset.giftGuideCategory;
      setPressed('[data-gift-guide-category]', category, 'giftGuideCategory', true);
    }
    if (budget === previousBudget && category === previousCategory) return;
    writeUrlFilters();
    // Let the indicator paint before filtering and measuring the product rows.
    cancelAnimationFrame(filterFrame);
    clearTimeout(filterTimer);
    filterFrame = requestAnimationFrame(() => {
      filterTimer = setTimeout(() => {
        if (!signal.aborted) update({ resetTracks: true, animate: true });
      }, 0);
    });
  }, { signal });

  resetButtons.forEach((button) => button.addEventListener('click', () => {
    const target = button.dataset.giftGuideReset;
    if (target === 'budget' || target === 'all') budget = 'all';
    if (target === 'category' || target === 'all') category = 'all';
    setPressed('[data-gift-guide-budget]', budget, 'giftGuideBudget');
    setPressed('[data-gift-guide-category]', category, 'giftGuideCategory');
    writeUrlFilters();
    update({ resetTracks: true, animate: true });
    const focusTarget = target === 'category'
      ? filters.querySelector('[data-gift-guide-category="all"]')
      : filters.querySelector('[data-gift-guide-budget="all"], [data-gift-guide-category="all"]');
    focusTarget?.focus();
  }, { signal }));

  window.addEventListener('popstate', () => {
    const next = readUrlFilters();
    const filtersChanged = next.budget !== budget || next.category !== category;
    budget = next.budget;
    category = next.category;
    setPressed('[data-gift-guide-budget]', budget, 'giftGuideBudget');
    setPressed('[data-gift-guide-category]', category, 'giftGuideCategory');
    if (next.needsSanitizing) writeUrlFilters({ replace: true });
    update({ resetTracks: filtersChanged, animate: filtersChanged });
  }, { signal });

  root.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest(`${SELECTORS.card} a[href]`);
    if (!(link instanceof HTMLAnchorElement) || !isProductUrl(link.href)) return;
    const rowPositions = {};
    rows.forEach((row, index) => {
      const key = row.dataset.rowKey || String(index);
      rowPositions[key] = carousels[index]?.read() || 0;
    });
    writeReturnState({
      guideUrl: currentGuideUrl(),
      budget,
      category,
      scrollY: window.scrollY,
      rows: rowPositions,
      createdAt: Date.now(),
    });
  }, { signal });

  const initial = readUrlFilters();
  budget = initial.budget;
  category = initial.category;
  if (initial.needsSanitizing) writeUrlFilters({ replace: true });

  const filterHint = filters.querySelector('[data-gift-guide-filter-hint]');
  const syncFilterHint = () => {
    if (filterHint) filterHint.hidden = ![...filters.querySelectorAll('.gift-guide__filter-scroll')]
      .some((scroll) => scroll.scrollWidth > scroll.clientWidth + 1);
  };
  const filterResizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => {
    setPressed('[data-gift-guide-budget]', budget, 'giftGuideBudget');
    setPressed('[data-gift-guide-category]', category, 'giftGuideCategory');
    syncFilterHint();
  }) : null;
  filters.querySelectorAll('.gift-guide__filter-scroll').forEach((scroll) => filterResizeObserver?.observe(scroll));

  instances.set(root, {
    cleanup: () => {
      transitionId += 1;
      stopRowMotion();
      abort.abort();
      cancelAnimationFrame(filterFrame);
      clearTimeout(filterTimer);
      filterResizeObserver?.disconnect();
      carousels.forEach((carousel) => carousel?.cleanup());
      instances.delete(root);
    },
  });
  root.dataset.giftGuideReady = 'true';
  filters.hidden = false;
  syncFilterHint();
  setPressed('[data-gift-guide-budget]', budget, 'giftGuideBudget');
  setPressed('[data-gift-guide-category]', category, 'giftGuideCategory');
  update();


  const returnState = readReturnState();
  if (returnState?.guideUrl === currentGuideUrl()
    && returnState.budget === budget && returnState.category === category
    && (performance.getEntriesByType('navigation')[0]?.type === 'back_forward' || isProductUrl(document.referrer))) {
    clearReturnState();
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
      rows.forEach((row, index) => {
        const key = row.dataset.rowKey || String(index);
        carousels[index]?.restore(Number(returnState.rows[key]));
        carousels[index]?.update();
      });
      window.scrollTo({ top: returnState.scrollY, behavior: 'auto' });
    }));
  }
};

const initWithin = (scope = document) => {
  if (scope.matches?.(SELECTORS.root)) initGiftGuide(scope);
  scope.querySelectorAll?.(SELECTORS.root).forEach(initGiftGuide);
};

initWithin();
document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  for (const [root, instance] of instances) {
    if (event.target === root || event.target.contains(root)) instance.cleanup();
  }
});
